import { useEffect, useMemo, useRef, useState } from "react";

/**
 * FretRail — a subtle vertical "guitar string" scroll indicator on the left of the page.
 * - Each section anchors a fret-marker dot.
 * - Filled string tracks scroll progress from the top of the sections.
 * - The BOOK marker (last one) pulses red after the user has scrolled past 50% of the page.
 * - Desktop-only (lg+). Silent smooth-scroll on click. Respects prefers-reduced-motion.
 * - Does not render inside .admin-page.
 */

const SECTIONS = [
  { id: "about", label: "Meet Sanjith" },
  { id: "courses", label: "Courses" },
  { id: "media", label: "Portfolio" },
  { id: "testimonials", label: "Testimonials" },
  { id: "contact", label: "Book a spot", cta: true },
];

const smooth = (id) => {
  const target = document.getElementById(id);
  if (!target) return;
  if (window.__lenis) window.__lenis.scrollTo(`#${id}`, { offset: -60, duration: 1.4 });
  else target.scrollIntoView({ behavior: "smooth" });
};

export const FretRail = () => {
  const [active, setActive] = useState(null);
  const [scrollPct, setScrollPct] = useState(0); // 0..1 of total page scroll
  const [visible, setVisible] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [railHover, setRailHover] = useState(false);
  const ticking = useRef(false);

  const reducedMotion = useMemo(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  // Track active section via IntersectionObserver + scroll fallback
  useEffect(() => {
    const els = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (els.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((e) => e.isIntersecting);
        if (visibleEntries.length) {
          const top = visibleEntries.reduce((a, b) => (a.intersectionRatio > b.intersectionRatio ? a : b));
          setActive(top.target.id);
        }
      },
      { threshold: [0, 0.15, 0.35, 0.6], rootMargin: "-15% 0px -55% 0px" }
    );
    els.forEach((el) => io.observe(el));

    // Fallback: on every scroll tick, if nothing is active, pick the section whose top is closest to 25% of viewport
    const pickByScroll = () => {
      const target = window.innerHeight * 0.25;
      let best = null;
      let bestDist = Infinity;
      for (const el of els) {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) continue;
        const dist = Math.abs(r.top - target);
        if (dist < bestDist) { bestDist = dist; best = el.id; }
      }
      if (best) setActive(best);
    };
    pickByScroll();
    window.addEventListener("scroll", pickByScroll, { passive: true });
    return () => { io.disconnect(); window.removeEventListener("scroll", pickByScroll); };
  }, []);

  // Track scroll % + show/hide (fade in after hero)
  useEffect(() => {
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        setScrollPct(Math.min(1, y / max));
        setVisible(y > 320);
        ticking.current = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hide inside admin
  if (typeof document !== "undefined" && document.querySelector(".admin-page")) return null;

  const shouldPulseBook = scrollPct > 0.5;

  return (
    <div
      data-testid="fret-rail"
      aria-hidden={!visible}
      className={`fret-rail hidden lg:flex fixed left-6 top-1/2 -translate-y-1/2 z-40 flex-col items-start gap-0 transition-opacity duration-500 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      onMouseEnter={() => setRailHover(true)}
      onMouseLeave={() => { setRailHover(false); setHoverIdx(null); }}
    >
      {/* small "Sections" kicker above the rail — signals it's a menu */}
      <span
        aria-hidden
        className={`absolute -top-8 left-0 font-mono text-[9px] tracking-[0.3em] uppercase text-[#4a3e30] transition-opacity duration-300 ${
          railHover ? "opacity-100" : "opacity-70"
        }`}
      >
        Sections
      </span>

      {/* thin string with red progress fill */}
      <div className="absolute left-[13px] top-2 bottom-2 w-[2px] bg-[rgba(28,22,16,0.14)] overflow-hidden rounded-full">
        <div
          className="w-full bg-[#DB0909] origin-top transition-transform duration-200 ease-out"
          style={{ height: "100%", transform: `scaleY(${scrollPct})` }}
        />
      </div>

      {SECTIONS.map((s, i) => {
        const isActive = active === s.id;
        const isHovered = hoverIdx === i;
        const isBook = !!s.cta;
        const shouldShowLabel = isActive || isHovered || (isBook && shouldPulseBook);
        const dotSize = isBook ? "w-[10px] h-[10px]" : isActive ? "w-[9px] h-[9px]" : "w-[7px] h-[7px]";
        const dotClass = isActive
          ? "bg-[#DB0909] border-[#DB0909] shadow-[0_0_0_3px_rgba(219,9,9,0.18)]"
          : "bg-[#f7f6f2] border-[rgba(28,22,16,0.4)] group-hover:border-[#DB0909]";
        return (
          <button
            key={s.id}
            data-testid={`fret-rail-${s.id}`}
            onClick={() => smooth(s.id)}
            onMouseEnter={() => setHoverIdx(i)}
            onFocus={() => setHoverIdx(i)}
            onBlur={() => setHoverIdx(null)}
            aria-label={`Jump to ${s.label}`}
            aria-current={isActive ? "true" : undefined}
            className="group relative flex items-center py-4 first:pt-2 last:pb-2 pr-3 cursor-pointer focus:outline-none"
          >
            <span
              className={`fret-dot relative z-10 rounded-full border-2 transition-all duration-300 ${dotSize} ${dotClass} ${
                isBook && shouldPulseBook && !reducedMotion ? "fret-book-pulse" : ""
              } group-focus-visible:shadow-[0_0_0_3px_rgba(219,9,9,0.25)]`}
            />
            <span
              className={`ml-4 font-mono text-[10px] tracking-[0.2em] uppercase whitespace-nowrap transition-all duration-300 ${
                shouldShowLabel
                  ? isBook
                    ? "opacity-100 translate-x-0 text-[#DB0909] font-bold"
                    : isActive
                      ? "opacity-100 translate-x-0 text-[#1c1610] font-bold"
                      : "opacity-100 translate-x-0 text-[#1c1610]"
                  : "opacity-0 -translate-x-2 pointer-events-none text-[#4a3e30]"
              }`}
            >
              {s.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};
